import { APP_SETTINGS } from '../config/appSettings'

export const IMAGE_CONFIG = APP_SETTINGS.images

export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const

export const IMAGE_FILE_ACCEPT = [
  ...ACCEPTED_IMAGE_TYPES,
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
].join(',')

export type AcceptedImageType = typeof ACCEPTED_IMAGE_TYPES[number]
export type AssetState =
  | 'local'
  | 'processing'
  | 'ready_to_upload'
  | 'uploading'
  | 'synced'
  | 'error'
  | 'deleted'
export type AssetErrorStage = 'processing' | 'upload' | 'delete' | null

export interface NoteAsset {
  id: string
  noteId: string
  kind: 'image'
  originalFileName: string | null
  originalMimeType: string
  masterMimeType: string | null
  thumbnailMimeType: string | null
  sourceBlob: Blob | null
  processedMasterBlob: Blob | null
  thumbnailBlob: Blob | null
  width: number | null
  height: number | null
  originalSize: number
  processedSize: number | null
  masterStoragePath: string | null
  thumbnailStoragePath: string | null
  state: AssetState
  errorStage: AssetErrorStage
  attempts: number
  nextAttemptAt: number
  lastError: string | null
  createdAt: number
  modifiedAt: number
}

export interface ProcessImageResult {
  master: Blob
  thumbnail: Blob | null
  width: number | null
  height: number | null
  masterMimeType: string
  thumbnailMimeType: string | null
  originalSize: number
  processedSize: number
}

export interface RemoteNoteAsset {
  id: string
  noteId: string
  kind: 'image'
  originalMimeType: string
  masterMimeType: string
  thumbnailMimeType: string | null
  width: number | null
  height: number | null
  originalSize: number
  processedSize: number
  masterStoragePath: string
  thumbnailStoragePath: string | null
  state: 'synced' | 'deleted'
  createdAt: number
  modifiedAt: number
}

export function createLocalImageAsset(
  id: string,
  noteId: string,
  file: File,
  now = Date.now(),
): NoteAsset {
  return {
    id,
    noteId,
    kind: 'image',
    originalFileName: file.name || null,
    originalMimeType: file.type,
    masterMimeType: null,
    thumbnailMimeType: null,
    sourceBlob: file,
    processedMasterBlob: null,
    thumbnailBlob: null,
    width: null,
    height: null,
    originalSize: file.size,
    processedSize: null,
    masterStoragePath: null,
    thumbnailStoragePath: null,
    state: 'local',
    errorStage: null,
    attempts: 0,
    nextAttemptAt: now,
    lastError: null,
    createdAt: now,
    modifiedAt: now,
  }
}

export function assetPreviewBlob(asset: NoteAsset, preferThumbnail = false): Blob | null {
  return preferThumbnail
    ? asset.thumbnailBlob ?? asset.processedMasterBlob ?? asset.sourceBlob
    : asset.processedMasterBlob ?? asset.sourceBlob ?? asset.thumbnailBlob
}

export function shouldOfferManualAssetRetry(
  asset: NoteAsset,
  now = Date.now(),
): boolean {
  return asset.state === 'error' && (
    asset.attempts >= IMAGE_CONFIG.manualRetryMinAttempts
    || now - asset.modifiedAt >= IMAGE_CONFIG.manualRetryDelayMs
  )
}

export function assetRequiresSynchronization(asset: NoteAsset): boolean {
  if (asset.state === 'synced') return false
  return !(
    asset.state === 'deleted'
    && asset.errorStage === null
    && asset.nextAttemptAt === Number.MAX_SAFE_INTEGER
  )
}

export function toRemoteNoteAsset(asset: NoteAsset): RemoteNoteAsset {
  if (
    !asset.masterMimeType
    || asset.processedSize === null
    || !asset.masterStoragePath
  ) {
    throw new Error('Asset is missing synchronized image metadata.')
  }
  return {
    id: asset.id,
    noteId: asset.noteId,
    kind: 'image',
    originalMimeType: asset.originalMimeType,
    masterMimeType: asset.masterMimeType,
    thumbnailMimeType: asset.thumbnailMimeType,
    width: asset.width,
    height: asset.height,
    originalSize: asset.originalSize,
    processedSize: asset.processedSize,
    masterStoragePath: asset.masterStoragePath,
    thumbnailStoragePath: asset.thumbnailStoragePath,
    state: asset.state === 'deleted' ? 'deleted' : 'synced',
    createdAt: asset.createdAt,
    modifiedAt: asset.modifiedAt,
  }
}
